import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Table,
  Form,
  InputGroup,
  Modal,
  Spinner,
} from "react-bootstrap";
import { getPeopleForUser, deletePerson } from "../../apeople";
import { AddPersonInline } from "./AddPersonInline";
import { EditPersonInline } from "./EditPersonInline";
import "../Services/Services.css";

const PEOPLE_TYPE_LABELS = {
  GP: "General Practitioner (GP)",
  Cardio: "Cardiologist",
  Derm: "Dermatologist",
  Endo: "Endocrinologist",
  Gastro: "Gastroenterologist",
  Neuro: "Neurologist",
  Onco: "Oncologist",
  Ortho: "Orthopaedic Surgeon",
  Pead: "Paediatrician",
  Phys: "Physician",
  Psych: "Psychiatrist",
  Pulmo: "Pulmonologist",

  ENT: "ENT Specialist",
  Eye: "Ophthalmologist",
  Radio: "Radiologist",
  Patho: "Pathologist",

  Physio: "Physiotherapist",
  OT: "Occupational Therapist",
  Biokineticist: "Biokineticist",
  Dietician: "Dietitian",
  Speech: "Speech Therapist",
  Audio: "Audiologist",
  Psychologist: "Clinical Psychologist",
  Chiro: "Chiropractor",
  Podiatrist: "Podiatrist",

  Nurse: "Nurse",
  Pharma: "Pharmacist",
  Dentist: "Dentist",

  Other: "Other",
};

const personId = (p) =>
  typeof p?._id === "string" ? p._id : p?._id?.$oid || p?.id;

const fullName = (p) =>
  `${p?.title ?? ""} ${p?.firstName ?? ""} ${p?.lastName ?? ""}`
    .replace(/\s+/g, " ")
    .trim();

const personEmails = (p) =>
  ["email", "email2", "email3", "email4", "email5"]
    .map((f) => (p?.[f] ?? "").trim())
    .filter(Boolean);

export function ViewPeople({ refresh, onPeopleUpdate = () => {}, addTrigger }) {
  const [people, setPeople] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [personToDelete, setPersonToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const addRef = useRef(null);
  const firstTriggerSeen = useRef(true);
  const lastTriggerRef = useRef(addTrigger);

  const reload = useCallback(async () => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      setPeople([]);
      return;
    }

    try {
      const data = await getPeopleForUser(token);
      setPeople(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load people:", err);
      setPeople([]);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload, refresh]);

  useEffect(() => {
    if (firstTriggerSeen.current) {
      firstTriggerSeen.current = false;
      return;
    }
    if (lastTriggerRef.current !== addTrigger) {
      setShowAdd(true);
      setEditingPersonId(null);
      lastTriggerRef.current = addTrigger;

      setTimeout(() => {
        addRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [addTrigger]);

  const handleEditClick = (id) => {
    setShowAdd(false);
    setEditingPersonId(id);
  };

  const exitEditMode = () => setEditingPersonId(null);

  const handleAdded = async () => {
    await reload();
    onPeopleUpdate();
    setShowAdd(false);
  };

  const handleEdited = async () => {
    await reload();
    onPeopleUpdate();
    setEditingPersonId(null);
  };

  const openDeleteConfirm = (person) => {
    setDeleteError("");
    setPersonToDelete(person);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setPersonToDelete(null);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!personToDelete) return;
    const id = personId(personToDelete);
    if (!id) return;

    const token = sessionStorage.getItem("user");
    if (!token) {
      setDeleteError("Session expired. Please sign in again.");
      return;
    }

    setDeleting(true);
    setDeleteError("");
    try {
      await deletePerson(id, token);
      setPersonToDelete(null);
      if (editingPersonId === id) setEditingPersonId(null);
      await reload();
      onPeopleUpdate();
    } catch (err) {
      setDeleteError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete person."
      );
    } finally {
      setDeleting(false);
    }
  };

  const filteredPeople = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (people ?? [])
      .filter((p) => {
        if (!q) return true;
        const typeLabel = PEOPLE_TYPE_LABELS[p?.type] || p?.type || "";
        return (
          (p?.firstName ?? "").toLowerCase().includes(q) ||
          (p?.lastName ?? "").toLowerCase().includes(q) ||
          typeLabel.toLowerCase().includes(q) ||
          (p?.bhfNumber ?? "").toString().toLowerCase().includes(q) ||
          (p?.hpcsaNumber ?? "").toString().toLowerCase().includes(q) ||
          personEmails(p).some((em) => em.toLowerCase().includes(q))
        );
      })
      .slice()
      .sort((a, b) => {
        const aName = `${a?.lastName ?? ""} ${a?.firstName ?? ""}`.toLowerCase();
        const bName = `${b?.lastName ?? ""} ${b?.firstName ?? ""}`.toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [people, searchQuery]);

  return (
    <Container fluid className="bg-transparent">
      {/* Search box */}
      <Row className="mb-3">
        <Col md={6} lg={5}>
          <InputGroup>
            <InputGroup.Text
              style={{
                backgroundColor: "#CFE6FF",
                border: "1px solid #CFE6FF",
                borderRight: "none",
              }}
            >
              <i className="fa-light fa-file-magnifying-glass text-primary" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search people by name, type, BHF or HPCSA number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ borderLeft: "none", height: "38px" }}
            />
            {searchQuery && (
              <InputGroup.Text
                onClick={() => setSearchQuery("")}
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #ced4da",
                  borderLeft: "none",
                  cursor: "pointer",
                }}
              >
                <i className="fa-solid fa-times text-muted" />
              </InputGroup.Text>
            )}
          </InputGroup>
        </Col>
      </Row>

      {/* Add inline panel */}
      <div ref={addRef} />
      {showAdd && (
        <Card className="mb-3 shadow-sm">
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{ background: "#CFE6FF" }}
          >
            <h6 className="mb-0 fw-bold text-primary">Add Person</h6>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => setShowAdd(false)}
            >
              Close
            </Button>
          </Card.Header>
          <Card.Body>
            <AddPersonInline
              onPeopleUpdate={handleAdded}
              onClose={() => setShowAdd(false)}
            />
          </Card.Body>
        </Card>
      )}

      {/* People table */}
      <Table bordered hover responsive className="fixed-table">
        <thead className="table-primary text-center align-middle">
          <tr>
            <th
              className="text-center align-middle text-primary"
              style={{ width: "26%" }}
            >
              Name
            </th>
            <th
              className="text-center align-middle text-primary"
              style={{ width: "21%" }}
            >
              Type
            </th>
            <th
              className="text-center align-middle text-primary"
              style={{ width: "24%" }}
            >
              Email
            </th>
            <th
              className="text-center align-middle text-primary"
              style={{ width: "11%" }}
            >
              BHF #
            </th>
            <th
              className="text-center align-middle text-primary"
              style={{ width: "11%" }}
            >
              HPCSA #
            </th>
            <th
              className="text-center align-middle text-primary"
              style={{ width: "7%" }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredPeople.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted py-4">
                {searchQuery
                  ? "No people match your search."
                  : "No people yet."}
              </td>
            </tr>
          )}

          {filteredPeople.map((p) => {
            const id = personId(p);
            const isEditing = editingPersonId === id;
            const name = fullName(p) || "Unnamed Person";

            return (
              <React.Fragment key={id}>
                <tr>
                  <td
                    className="services-row-cell-truncate fw-bold text-primary text-truncate"
                    title={name}
                  >
                    {name}
                  </td>
                  <td className="services-row-cell-truncate text-truncate" title={PEOPLE_TYPE_LABELS[p?.type] || p?.type || ""}>
                    {PEOPLE_TYPE_LABELS[p?.type] || p?.type || "-"}
                  </td>
                  <td
                    className="services-row-cell-truncate text-truncate"
                    title={personEmails(p).join(", ")}
                  >
                    {(() => {
                      const emails = personEmails(p);
                      if (!emails.length) return "-";
                      return (
                        <>
                          {emails[0]}
                          {emails.length > 1 && (
                            <span className="text-muted small ms-1">
                              +{emails.length - 1}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td>{p?.bhfNumber ?? "-"}</td>
                  <td>{p?.hpcsaNumber ?? "-"}</td>
                  <td className="text-end">
                    {isEditing ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        type="button"
                        onClick={exitEditMode}
                        aria-label="Close editor"
                        title="Close editor"
                      >
                        Close
                      </Button>
                    ) : (
                      <div className="d-flex justify-content-end align-items-center gap-0">
                        <Button
                          variant="link"
                          type="button"
                          onClick={() => handleEditClick(id)}
                          aria-label="Edit"
                          title="Edit"
                          className="p-1"
                        >
                          <i className="fas fa-edit text-primary" />
                        </Button>
                        <Button
                          variant="link"
                          type="button"
                          onClick={() => openDeleteConfirm(p)}
                          aria-label="Delete"
                          title="Delete"
                          className="p-1"
                        >
                          <i className="fas fa-trash text-danger" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>

                {isEditing && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <div className="service-edit-cover">
                        <EditPersonInline
                          personId={id}
                          initialValue={p}
                          onHide={exitEditMode}
                          onPeopleUpdate={handleEdited}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>

      <Modal
        show={!!personToDelete}
        onHide={closeDeleteConfirm}
        centered
        backdrop={deleting ? "static" : true}
      >
        <Modal.Header closeButton={!deleting} style={{ background: "#CFE6FF" }}>
          <Modal.Title className="fw-bold text-primary h6 mb-0">
            Delete person
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {personToDelete && (
            <p className="mb-0">
              Are you sure you want to delete{" "}
              <strong>{fullName(personToDelete) || "this person"}</strong>?
            </p>
          )}
          {deleteError && (
            <div className="text-danger small mt-2">{deleteError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeDeleteConfirm}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
